import React from 'react';
import { Separator } from '@/components/ui/separator';
import { formatDateTime12, formatDateTime24 } from '@/utils/helpers';
import type { AlertTriageListRow } from './PulseOximeterAlertView';

const nonEmpty = (v: unknown) =>
  v != null && String(v).trim() !== "" ? String(v).trim() : "";

interface GlucoseMeterAlertViewProps {
  data: any;
  listRow?: AlertTriageListRow | null;
}

const GlucoseMeterAlertView: React.FC<GlucoseMeterAlertViewProps> = ({ data, listRow }) => {
  const glucoseMeterData = data?.data?.forms?.[0]?.glucoseMeterData;
  if (!glucoseMeterData?.triageDetails) return null;

  const formData = data?.data?.forms?.[0];
  const patient = formData?.patientId;
  const patientName = patient ? [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ') : '';
  const timestampRaw =
    formData?.createdAt ?? data?.data?.createdAt ?? data?.data?.triageTime;
  const timestamp = timestampRaw ? formatDateTime12(timestampRaw) : '';
  const timestampSafe = nonEmpty(listRow?.timestamp) || timestamp || "—";
  const deviceTypeRaw = formData?.deviceType ?? data?.data?.deviceType ?? '';
  const deviceDisplay = deviceTypeRaw
    ? deviceTypeRaw
        .replace(/_/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : '';
  const deviceDisplaySafe = nonEmpty(listRow?.device) || deviceDisplay || "—";

  const readingValues = formData?.readingValues ?? data?.data?.readingValues ?? {};
  const readingValuesFormatted =
    readingValues && typeof readingValues === 'object' && !Array.isArray(readingValues)
      ? Object.entries(readingValues)
          .map(([key, value]) => {
            const formattedKey =
              key && typeof key === 'string'
                ? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
                : String(key);
            return `${formattedKey}: ${value}`;
          })
          .join(', ')
      : '';
  const readingId = formData?.readingId ?? data?.data?.readingId;
  const readingFromId =
    typeof readingId === 'object' && readingId !== null
      ? readingId?.readingId ?? readingId?._id ?? ''
      : readingId ?? '';
  const readingDisplay = readingValuesFormatted || (readingFromId ? String(readingFromId) : '');
  const readingDisplaySafe =
    nonEmpty(listRow?.reading) || readingDisplay || "—";
  const submittedBy = formData?.submittedBy;
  const submittedByName = submittedBy?.name || '';

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4">
      <div className="bg-white shadow-lg" style={{ 
        width: '595px', 
        maxWidth: '100%', 
        margin: '0 auto', 
        boxSizing: 'border-box',
        padding: '40px',
        minHeight: '842px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <div className="header mb-8 bg-[#eeeeee] p-5 rounded-md">
          <h2 className="font-medium teachers mb-4 text-lg">
            Triage View
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patientName && (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">PATIENT NAME:</span>
                <span className="ml-2 text-black text-sm teachers">{patientName}</span>
              </div>
            )}
            {submittedByName && (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">SUBMITTED BY:</span>
                <span className="ml-2 text-black text-sm teachers">{submittedByName}</span>
              </div>
            )}
          </div>
          {submittedByName && (
            <div className="mt-4 flex flex-col gap-2">
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">READING:</span>
                <span className="ml-2 text-black text-sm teachers">{readingDisplaySafe}</span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">DEVICE:</span>
                <span className="ml-2 text-black text-sm teachers">{deviceDisplaySafe}</span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">TIMESTAMP:</span>
                <span className="ml-2 text-black text-sm teachers">{timestampSafe}</span>
              </div>
            </div>
          )}
        </div>
        <h3 className="font-medium teachers mb-6 text-base">
          Glucose Meter Triage Form
        </h3>
        <div className="space-y-0">
          <div className="mb-4">
            <div className="teachers text-xs mb-2.5">
              Please enter the details of the Glucose Monitor Triage.
            </div>
            <div className="font-medium teachers text-xs">
              {glucoseMeterData.triageDetails}
            </div>
            <Separator className='bg-[#dddddd] my-2'/>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlucoseMeterAlertView;
